/**
 * RecordCleaningModal — record a cleaning + disinfection event (migration
 * 0100). Pick a run (or "whole area" + label), the product used and an
 * optional note. Used from the Today board and the Runs settings page.
 */
import { Modal } from '@/components/Modal';
import { createCleaningLogEntry } from '@/lib/api';
import { X } from 'lucide-react';
import { useState } from 'react';

export interface RecordCleaningModalProps {
  facilityId: string;
  runs: ReadonlyArray<{ id: string; name: string }>;
  /** Pre-select a run (Runs settings row action). */
  initialRunId?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

const WHOLE_AREA = '__area__';

export function RecordCleaningModal({
  facilityId,
  runs,
  initialRunId,
  onClose,
  onSaved,
}: RecordCleaningModalProps) {
  const [runId, setRunId] = useState<string>(initialRunId ?? runs[0]?.id ?? WHOLE_AREA);
  const [areaLabel, setAreaLabel] = useState('');
  const [productUsed, setProductUsed] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isArea = runId === WHOLE_AREA;

  const submit = async () => {
    if (isArea && areaLabel.trim().length === 0) {
      setError('Give the area a name (e.g. Kitchen, Corridor).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createCleaningLogEntry({
        facilityId,
        runId: isArea ? null : runId,
        ...(isArea ? { areaLabel: areaLabel.trim() } : {}),
        productUsed: productUsed.trim() ? productUsed.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the clean.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm';
  const labelCls =
    'mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground';

  return (
    <Modal onClose={onClose} aria-label="Record cleaning">
      <div className="w-[min(96vw,30rem)] rounded-xl bg-background p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Record cleaning</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Logged against you, now. Inspectors ask for per-unit cleaning evidence.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="clean-run" className={labelCls}>
              Run / area
            </label>
            <select
              id="clean-run"
              value={runId}
              onChange={(e) => setRunId(e.target.value)}
              className={inputCls}
            >
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
              <option value={WHOLE_AREA}>Whole area / other</option>
            </select>
          </div>
          {isArea && (
            <div>
              <label htmlFor="clean-area" className={labelCls}>
                Area name
              </label>
              <input
                id="clean-area"
                type="text"
                value={areaLabel}
                onChange={(e) => setAreaLabel(e.target.value)}
                maxLength={120}
                placeholder="Kitchen, corridor, isolation block…"
                className={inputCls}
              />
            </div>
          )}
          <div>
            <label htmlFor="clean-product" className={labelCls}>
              Product used
            </label>
            <input
              id="clean-product"
              type="text"
              value={productUsed}
              onChange={(e) => setProductUsed(e.target.value)}
              maxLength={200}
              placeholder="Disinfectant name and dilution"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="clean-notes" className={labelCls}>
              Note
            </label>
            <textarea
              id="clean-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              className={inputCls}
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="press rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="press rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Record clean'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
